import createDOMPurify, { type DOMPurify } from "dompurify";

import { Subscription, Customer, ContentfulProduct, ProductGrowthPlanData } from "@/types/common";
import { Categories } from "@/types/search";

const matchBrackets = /[^{{]+(?=}})/g;
const stripAllTags = /<[^>]+>/g;
const splitParagraphs = /<\/p>/i;
const stripOpeningParagraph = /^<p[^>]*>/i;

const ZENDESK_HTML_CONFIG = {
  ALLOWED_TAGS: ["a"],
  ALLOWED_ATTR: ["href", "target", "rel"],
  ALLOWED_URI_REGEXP: /^(?:https?:\/\/|\/(?!\/))/i,
};

const isExternalHref = (href: string | null): boolean => {
  if (!href || href.startsWith("/")) return false;

  try {
    const { hostname } = new URL(href);
    return !/(^|\.)nutrafol\.com$/i.test(hostname);
  } catch {
    return false;
  }
};

const configureLinkTarget = (node: Element): void => {
  if (node.tagName !== "A" || !node.hasAttribute("href")) return;

  const href = node.getAttribute("href");

  if (isExternalHref(href)) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
    return;
  }

  node.removeAttribute("target");
  node.removeAttribute("rel");
};

let domPurify: DOMPurify | null = null;

const getDomPurify = (): DOMPurify | null => {
  if (typeof window === "undefined") return null;

  if (!domPurify) {
    domPurify = createDOMPurify(window);
    domPurify.addHook("afterSanitizeAttributes", configureLinkTarget);
  }

  return domPurify;
};

const sanitizeChunk = (chunk: string): string => {
  const purify = getDomPurify();
  if (purify) return purify.sanitize(chunk, ZENDESK_HTML_CONFIG);

  /* SSR fallback: strip all tags (article body only renders client-side) */
  return chunk.replace(stripAllTags, "");
};

export const pdpPathToSku: Record<string, string> = {
  "/hair/women/product/core/women-advanced/": "ADV",
  "/hair/women/product/core/balance/": "BAL",
  "/hair/women/product/core/postpartum/": "POS",
  "/hair/men/product/core/men/": "MENPRO",
  "/hair/men/product/core/men50/": "MSR",

  //BOOSTERS
  "/hair/men/product/booster/hair-growth-resilience-duo/": "RESILIENCE",
  "/hair/men/product/booster/hair-growth-synbiosis-duo/": "SYNBIOSIS",
  "/hair/men/product/booster/hair-growth-strength-duo/": "STRENGTH",
  "/hair/men/product/booster/hair-growth-shield-duo/": "SHIELD",
  "/hair/men/product/booster/hair-growth-longevity-duo/": "LONGEVITY",
  "/hair/men/product/booster/hair-growth-defense-duo/": "DEFENSE",
  "/hair/women/product/booster/stress-adaptogen/": "RESILIENCE",
  "/hair/women/product/booster/gut-microbiome/": "SYNBIOSIS",
  "/hair/women/product/booster/collagen/": "STRENGTH",
  "/hair/women/product/booster/hormone-support/": "EQUILIBRIUM",
  "/hair/women/product/booster/toxin-cleanse/": "SHIELD",
  "/hair/women/product/booster/energy/": "LONGEVITY",

  //SCALP
  "/hair/men/product/kit/core-hair-serum/": "MHAIRSERUMSML",
  "/hair/women/product/scalp/shampoo/": "RPURIFIER",
  "/hair/women/product/scalp/conditioner/": "SDEFENDER",
  "/hair/women/product/scalp/hair-serum/": "HAIRSERUMSML",
  "/hair/women/product/scalp/exfoliator-mask/": "BBLOCKER",
};

export const reformatProducts = (products: { title: string; categories: { sku: string }[] }[]) => {
  const categories: Record<string, string[]> = {};

  products.forEach((product) => {
    const key = product.title.split(" ")[0].toLowerCase();
    categories[key] = product.categories.map((cat) => cat?.sku);
  });

  return categories;
};

export const getSubscriptionState = (subscriptionsAndOrders: {
  subscriptions?: Subscription[];
  customer?: Customer;
}): string => {
  const { subscriptions, customer } = subscriptionsAndOrders || {};

  if (subscriptions?.length) {
    const hasActiveSubscription = subscriptions.find((sub) => sub.status === "active");
    return hasActiveSubscription ? "subscriber" : "previously subscribed";
  }

  if (customer?.orders?.items?.length) {
    return "one-time purchase";
  }

  return "default";
};

type SubscriptionSKUsByStatus = {
  active: string[];
  canceled: string[];
  single: string[];
  payment_failed: string[];
};

export const reformatSubscriptions = ({
  subscriptions,
  customer,
}: {
  subscriptions?: Subscription[];
  customer?: Customer;
}) => {
  const userSubscriptionsBySku: SubscriptionSKUsByStatus = {
    active: [],
    canceled: [],
    single: [],
    payment_failed: [],
  };

  subscriptions?.forEach((sub) => {
    const product_growth_plan_data: ProductGrowthPlanData = Object.values(
      JSON.parse(sub.product_growth_plan_data)
    )[0];
    const subSkus = Object.keys(product_growth_plan_data?.child_products || {});

    if (sub.status in userSubscriptionsBySku) {
      subSkus.forEach((sku) => {
        userSubscriptionsBySku[sub.status as keyof SubscriptionSKUsByStatus].push(sku);
      });
    }
  });

  customer?.orders?.items?.forEach((order) => {
    order.items.forEach((item) => {
      if (item.billing_interval === "") {
        userSubscriptionsBySku.single.push(item.product_sku);
      }
    });
  });

  return userSubscriptionsBySku;
};

export const getFormattedButton = (button: string): [string, string] => {
  const parts = button.split("_");
  const url = parts[parts.length - 1];
  const text = parts.slice(1, parts.length - 1).join(" ");
  return [text, url];
};

export const stripZendeskSpecialStrings = (body: string): string => {
  if (!body) return "";

  const reformatted = body
    .split(splitParagraphs)
    .map((paragraph) => paragraph.replace(stripOpeningParagraph, "").trim())
    .filter(Boolean)
    .map((paragraph) => sanitizeChunk(paragraph))
    .filter((i) => i.length > 3)
    .map((i) => `<p>${i}</p>`)
    .join("")
    .replace(matchBrackets, "");

  return reformatted.split("<p>{{}}</p>").join("");
};

export const getInteractiveElements = (body: string): RegExpMatchArray | null => {
  return body?.match(matchBrackets);
};

export const filterCategoriesByType = (
  categories: [string, ContentfulProduct[]][],
  option: string[]
): [string, ContentfulProduct[]][] => {
  return categories.filter(([category]) => option.includes(category));
};

export const getFilteredCategories = (
  categories: Categories,
  subscriptions: Record<string, string[]>
): [string, ContentfulProduct[]][] => {
  const categoryTypes = {
    core: "Hair Growth Supplements",
    booster: "Wellness Boosters",
    scalp: "Scalp Care",
  };

  const options = {
    subscriber: [categoryTypes.booster, categoryTypes.scalp],
    skinSubscriber: [categoryTypes.core, categoryTypes.booster],
    cancelled: [categoryTypes.core, categoryTypes.scalp],
    default: [categoryTypes.core],
  };

  const categoryArray = Object.entries(categories);

  if (subscriptions?.active?.length) {
    const isSkinSubscriber = subscriptions.active.some((sku) => sku.includes("SKIN"));
    return filterCategoriesByType(
      categoryArray,
      isSkinSubscriber ? options.skinSubscriber : options.subscriber
    );
  }

  if (
    subscriptions?.canceled?.length ||
    subscriptions?.payment_failed?.length ||
    subscriptions?.single?.length
  ) {
    return filterCategoriesByType(categoryArray, options.cancelled);
  }

  return filterCategoriesByType(categoryArray, options.default);
};

export const getPdpVisitSkus = (): string[] => {
  const pdpVisits = JSON.parse(
    typeof window !== "undefined" ? localStorage.getItem("pdpVisits") : "null"
  );
  if (pdpVisits) {
    return (
      Object.entries(pdpVisits)
        // @ts-expect-error - this is a workaround
        .sort((a, b) => b[1] - a[1])
        .map(([path]) => pdpPathToSku[path])
        .filter((sku) => sku)
    );
  }
  return [];
};

export const getQuizResultsInfoSkus = (): string[] => {
  const quizResultsInfo = JSON.parse(
    typeof window !== "undefined" ? localStorage.getItem("quizResultsInfo") : "null"
  );
  return quizResultsInfo?.skus || [];
};

type ProductSortable = {
  orderQty?: number;
  sku: string;
};

export function sortProducts<T extends ProductSortable>(
  products: T[] = [],
  purchasedSkus: string[] = []
): T[] {
  const sortedProducts = [...products].sort((a, b) => a.orderQty - b.orderQty);
  const sortingSkus = [...purchasedSkus, ...getPdpVisitSkus(), ...getQuizResultsInfoSkus()];

  return sortedProducts.sort((a, b) => {
    for (const sku of sortingSkus) {
      const skuBase = sku.split("-")[0];
      if (a.sku.includes(skuBase)) return -1;
      if (b.sku.includes(skuBase)) return 1;
    }
    return 0;
  });
}

export const getCanceledOrders = (
  items: { title: string; categories: { sku: string }[] }[],
  subscriptions: Record<string, string[]>
): string[] => {
  const skus = reformatProducts(items);
  const canceledProduct: string[] = [];

  Object.keys(skus).forEach((category) => {
    skus[category].forEach((product) => {
      if (subscriptions?.canceled.includes(product)) {
        canceledProduct.push(product);
      }
    });
  });

  return canceledProduct;
};

export const matchAlgoliaToContentful = (
  contentfulData: ContentfulProduct[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  algoliaData: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[] => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const products: Record<string, any> = {};
  contentfulData?.forEach((product) => {
    const temp = { ...product };
    products[product?.sku] = temp;
  });
  return algoliaData
    ?.map((product) => {
      if (products[product.sku]) {
        return { ...product, ...products[product.sku] };
      }
    })
    .filter(Boolean); // filter out undefined
};
